import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Loader2 } from 'lucide-react';
import type { ConditionSuggestionCategory, CategoryFormData } from '../../../api/condition-suggestions.api';

interface CategoryFormModalProps {
  /** Open state of the dialog. */
  open: boolean;
  /** Called when the dialog requests to close (ESC, backdrop click, cancel button). */
  onClose: () => void;
  /**
   * Category to edit. When `null` the modal operates in "create" mode.
   * When set, all form fields are pre-filled from the category.
   */
  editingCategory: ConditionSuggestionCategory | null;
  /** Called after a successful save. Parent should refresh its list. */
  onSaved: () => void;
  /** Async create handler — resolves on success, throws on backend error. */
  onCreate: (dto: CategoryFormData) => Promise<ConditionSuggestionCategory>;
  /** Async update handler — resolves on success, throws on backend error. */
  onUpdate: (id: string, dto: Partial<CategoryFormData>) => Promise<ConditionSuggestionCategory>;
}

interface FormState {
  name: string;
  description: string;
  displayOrder: number;
}

interface FormErrors {
  name?: string;
  displayOrder?: string;
}

const EMPTY_FORM: FormState = {
  name: '',
  description: '',
  displayOrder: 0,
};

export function CategoryFormModal({
  open,
  onClose,
  editingCategory,
  onSaved,
  onCreate,
  onUpdate,
}: CategoryFormModalProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Sync form when switching between create / edit
  useEffect(() => {
    if (open) {
      if (editingCategory) {
        setForm({
          name: editingCategory.name,
          description: editingCategory.description ?? '',
          displayOrder: editingCategory.displayOrder,
        });
      } else {
        setForm(EMPTY_FORM);
      }
      setErrors({});
      setServerError(null);
    }
  }, [open, editingCategory]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.name.trim()) {
      next.name = 'Name ist erforderlich.';
    } else if (form.name.trim().length > 100) {
      next.name = 'Name darf maximal 100 Zeichen lang sein.';
    }
    if (form.displayOrder < 0 || !Number.isInteger(form.displayOrder)) {
      next.displayOrder = 'Reihenfolge muss eine positive ganze Zahl sein.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setIsSaving(true);
    setServerError(null);

    const dto: CategoryFormData = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      displayOrder: form.displayOrder,
    };

    try {
      if (editingCategory) {
        await onUpdate(editingCategory.id, dto);
      } else {
        await onCreate(dto);
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      const apiError = err as { message?: string; statusCode?: number };
      if (Array.isArray(apiError.message)) {
        // NestJS class-validator sends an array for validation errors
        setServerError((apiError.message as string[]).join(' '));
      } else {
        setServerError(apiError.message ?? 'Fehler beim Speichern.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isEditing = editingCategory !== null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-lg" aria-describedby="category-modal-desc">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
          </DialogTitle>
          <DialogDescription id="category-modal-desc">
            {isEditing
              ? 'Bearbeiten Sie die Felder der Kategorie und speichern Sie.'
              : 'Erstellen Sie eine neue Kategorie für Bedingungs-Vorschläge.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1">
            <Label htmlFor="cat-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="cat-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              maxLength={100}
              placeholder="z.B. Geschäftszeiten"
              aria-invalid={!!errors.name}
            />
            {errors.name && (
              <p className="text-xs text-red-600" role="alert">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label htmlFor="cat-description">Beschreibung</Label>
            <Textarea
              id="cat-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optionale Beschreibung, welche Bedingungstypen in diese Kategorie gehören..."
              rows={3}
            />
          </div>

          {/* Display Order */}
          <div className="space-y-1">
            <Label htmlFor="cat-order">Anzeigereihenfolge</Label>
            <Input
              id="cat-order"
              type="number"
              min={0}
              value={form.displayOrder}
              onChange={(e) => setForm({ ...form, displayOrder: parseInt(e.target.value, 10) || 0 })}
              aria-invalid={!!errors.displayOrder}
            />
            <p className="text-xs text-gray-500">
              Niedrigere Werte werden zuerst angezeigt.
            </p>
            {errors.displayOrder && (
              <p className="text-xs text-red-600" role="alert">{errors.displayOrder}</p>
            )}
          </div>

          {/* Server error */}
          {serverError && (
            <div
              className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
              role="alert"
            >
              {serverError}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Speichern...
              </>
            ) : isEditing ? (
              'Speichern'
            ) : (
              'Erstellen'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
